import clsx from 'clsx';
import { MenuItem } from 'components/Menu/MenuItem';
import { Menu } from 'components/Menu';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';
import { MoreHIcon } from 'components/icons/MoreHIcon';
import { ModalContext } from 'context/ModalContext';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { TrashIcon } from 'components/icons/TrashIcon';
import { ImageIcon } from 'components/icons/ImageIcon';
import { FilmEditIcon } from 'components/icons/FilmEditIcon';
import { BarChartIcon } from 'components/icons/BarChartIcon';
import { SurveyIcon } from 'components/icons/SurveyIcon';
import { FolderIcon } from 'components/icons/FolderIcon';
import { ProfilePicIcon } from 'components/icons/ProfilePicIcon';
import { Share2Icon } from 'components/icons/ShareIcon2';
import { SquareInfoIcon } from 'components/icons/SquareInfoIcon';
import { v4 as uuid } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import { DownloadIcon } from 'components/icons/DownloadIcon';
import api from 'services/api';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';

interface IVideoButtons {
  videoId: string;
  workspaceId?: string;
  video: any;
  isDownloadable?: boolean;
}

type menuItem = {
  id: string;
  icon: JSX.Element;
  name: string;
  onClick?: () => void;
};

function VideoButtons({ videoId, workspaceId, video, isDownloadable }: IVideoButtons) {
  const seekTimestamp = useSelector((state: RootState) => state.videoSeektime);

  const location = useLocation();
  const { showModal } = useContext(ModalContext);
  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryChannelVideos);
  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';
  const getVideo = secondaryViewVideos.channels[channelId];
  const videoObject = secondaryViewVideos.channels[channelId]?.lastVideoData;

  const videoShots = useSelector((state: RootState) => state.videoShots.videos[videoId as string]);
  const [currentVideoShot, setCurrentVideoShot] = useState(seekTimestamp.videoSeek.videoShotIdx);

  const handleSharingOptions = (value: string) => {
    const updatedVideoData = { ...video, sharing_option: value };
    setCurrentVideoShot(updatedVideoData);
  };

  const [timestamp, setTimestamp] = useState<number | null>(null);
  useEffect(() => {
    if (getVideo && getVideo?.playerRef) {
      const time = getVideo?.playerRef?.getCurrentTime();
      setTimestamp(time);
    }
  }, []);

  const shareExternal = useCallback(() => {
    if (video) {
      showModal(MODAL_TYPES.external_sharing, {
        animation: MODAL_ANIMATIONS.unfold,
        videoId: video.id,
        videoHashId: video.hash_id ? video.hash_id : video.hashId,
        videoName: video.name,
        sharingOption: video.sharing_option,
        setVideoSharing: handleSharingOptions,
        timestamp: timestamp,
        videoShots: videoShots
      });
    }
  }, [video]);

  const initialMenus: menuItem[] = []; // Specify the type for the initial state
  const [menus, setMenus] = useState<menuItem[]>(initialMenus);
  const navigateTo = useNavigate();

  // const workspaces = localStorage.getItem('workspaces') || '[]';
  // const sessionWorkspace = sessionStorage.getItem('workspace') || localStorage.getItem('last-active:workspace');
  // const activeWorkspace = _.find(JSON.parse(workspaces), { workspace_id: sessionWorkspace });

  function handleDownload(videoHashId: string) {
    const videoShotHashId = videoShots[videoObject?.selectedShot || 0].shot_hash_id;
    api.get(`/api/videos/${videoHashId}/shots/${videoShotHashId}/share/download-link/`).then((res) => {
      window.open(res.data.download_link, '_blank', 'noopener,noreferrer');
    });
  }

  useEffect(() => {
    function handleOnClick(videoId: string, path: string) {
      navigateTo(`/workspace/${workspaceId}/studio/${videoId}/${path}`);
    }

    if (videoId) {
      const options: menuItem[] = [
        {
          id: uuid(),
          icon: <SquareInfoIcon className="mr-3 shrink-0 h-5 w-5 stroke-black group-hover:stroke-white" />,
          name: 'Video details',
          onClick: () => handleOnClick(videoId, 'video-details')
        },
        {
          id: uuid(),
          icon: <Share2Icon className="mr-3 shrink-0 h-4 w-5 group-hover:stroke-white group-hover:fill-none" />,
          name: 'External sharing link',
          onClick: () => {
            shareExternal();
          }
        },
        {
          id: uuid(),
          icon: <ProfilePicIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Speaker info',
          onClick: () => handleOnClick(videoId, 'speaker-info')
        },
        {
          id: uuid(),
          icon: <FolderIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Resources',
          onClick: () => handleOnClick(videoId, 'resources')
        },
        {
          id: uuid(),
          icon: <SurveyIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Polls',
          onClick: () => handleOnClick(videoId, 'polls')
        },
        {
          id: uuid(),
          icon: <BarChartIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Analytics',
          onClick: () => handleOnClick(videoId, 'analytics')
        },
        {
          id: uuid(),
          icon: <FilmEditIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Edit and mix',
          onClick: () => handleOnClick(videoId, 'edit-mix')
        },
        {
          id: uuid(),
          icon: <ImageIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Background image for mix',
          onClick: () => handleOnClick(videoId, 'bg-mix')
        },
        {
          id: uuid(),
          icon: <TrashIcon className="mr-3 shrink-0 h-5 w-5 fill-black group-hover:fill-white" />,
          name: 'Delete',
          onClick: () =>
            showModal(MODAL_TYPES.delete_video, {
              animation: MODAL_ANIMATIONS.fade,
              videoId: video.id,
              videoHashId: video.hash_id,
              videoName: video.name
            })
        }
      ];
      setMenus(options);
    }
  }, []);

  return (
    <div className="video-button-container flex items-center flex-wrap ml-auto relative">
      {/* SHARE TO CHANNEL */}
      <div className="flex ml-auto items-center">
        <div
          className={clsx(
            'flex bg-gray-100 pl-1 pr-3 h-[36px] items-center justify-center rounded-[18px] ml-2 cursor-pointer  hover:bg-gray-200 hover:bg-opacity-50'
          )}
          onClick={shareExternal}
        >
          <ReplyOutlinedIcon className="w-10 h-6 transform scale-x-[-1] ml-2" />
          <span className="text-[14px] ml-1 font-bold">Share</span>
        </div>

        {video && video.status === 'success' && videoShots?.length && isDownloadable && (
          <>
            <div
              className={clsx(
                'flex bg-gray-100 px-3 h-[36px] items-center justify-center rounded-[18px] ml-2 cursor-pointer hover:bg-gray-200 hover:bg-opacity-50'
              )}
              onClick={() => handleDownload(video.hash_id)}
            >
              <DownloadIcon className="w-8 h-6 fill-iconColor pr-2 font-normal" />
              <span className="text-[14px] font-bold">Download</span>
            </div>

            <div
              className={clsx(
                'flex bg-gray-100 px-3 h-[36px] items-center justify-center rounded-[18px] ml-2 cursor-pointer  hover:bg-gray-200 hover:bg-opacity-50'
              )}
              onClick={() =>
                showModal(MODAL_TYPES.statistics, {
                  videoHashId: video.hash_id,
                  channelHashId: channelId,
                  channelName: ''
                })
              }
            >
              <span className="text-[14px] font-bold">Stats</span>
            </div>
          </>
        )}

        <Menu
          menus={menus}
          align="center"
          position="absolute"
          top="mt-[460px] bottom-12 -ml-[100px]"
          width={265}
          MenuItem={MenuItem}
          additionalClasses="show-more-container flex justify-center w-fit"
        >
          <div
            className={clsx(
              'show-more-container flex p-3 bg-gray-100 items-center justify-center rounded-full ml-2 cursor-pointer   hover:bg-gray-200 hover:bg-opacity-50'
            )}
          >
            <MoreHIcon className="w-4 h-4 rotate-90" />
          </div>
        </Menu>
      </div>
    </div>
  );
}
export default VideoButtons;
